import FilterManagement from "../../../AppView/FilterManagement/FilterManagement";
import ScopeManagement from "../../../AppView/ScopeManagement/ScopeManagement";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { FiltersContext } from "../../../AppView/FilterManagement/FilterProvider/FilterProvider";
import { useContext, useEffect } from "react";
import {
  ScopesContext,
  type SelectedScope,
} from "../../../AppView/ScopeManagement/ScopeProvider/ScopeProvider";
import "./FilterAndScopeInputs.scss";
import FindingsBySeverityBar, {
  type FindingsBySeverityProps,
} from "./FindingsBySeverityBar/FindingsBySeverityBar";
import { EMPTY_FILTER } from "../../../AppView/FilterManagement/FiltersMenu/FiltersMenu";

import { REQUIRED_ERROR_MESSAGE } from "@SeemplicityDev/shared";
import { STATUS_FILTER_TO_PIN } from "../../../Findings/FindingsFilters/FilterUtils";
import type { FilterObject } from "../../../AppView/FilterManagement/CommonFilterTypes";

interface FilterAndScopeFormValues {
  filter?: FilterObject;
  scope?: SelectedScope;
}

export interface FilterAndScopeRequiredMessages {
  scope: string;
  filter: string;
}

interface FilterAndScopeInputsProps {
  isSummary?: boolean;
  findingsCountViewMode?: boolean;
  requiredMessages?: FilterAndScopeRequiredMessages;
}

const FilterAndScopeInputs: React.FC<FilterAndScopeInputsProps> = ({
  isSummary = false,
  findingsCountViewMode = false,
  requiredMessages,
}) => {
  const { control, setValue, trigger } =
    useFormContext<FilterAndScopeFormValues>();

  const {
    filtersState: { selectedFilter },
    updateFiltersState,
    getFilterValueById,
    filtersEntity,
  } = useContext(FiltersContext);
  const {
    scopesState: { selectedScope },
    updateScopeState,
    getScopeValueById,
  } = useContext(ScopesContext);

  const formFilter = useWatch({ control, name: "filter" });
  const formScope = useWatch({ control, name: "scope" });

  // ** Update form values when contexts change ** //
  useEffect(() => {
    if (selectedFilter && selectedFilter?.label !== EMPTY_FILTER) {
      setValue("filter", selectedFilter);
      trigger("filter");
    }
  }, [selectedFilter]);

  useEffect(() => {
    if (selectedScope) {
      setValue("scope", selectedScope);
      trigger("scope");
    }
  }, [selectedScope]);

  // ** Update contexts when form values change if contexts are not set ** //
  useEffect(() => {
    if (
      formFilter &&
      (!selectedFilter || selectedFilter?.label === EMPTY_FILTER)
    ) {
      let filter: FilterObject | undefined;
      if (formFilter?.value?.id) {
        filter = getFilterValueById(formFilter?.value?.id);
      } else {
        // label/query may be undefined at runtime (preserved from JS) —
        // cast keeps the original pass-through shape.
        filter = {
          label: formFilter?.label,
          value: {
            query: formFilter?.value?.query,
          },
        } as FilterObject;
      }

      if (filter) {
        updateFiltersState({ selectedFilter: filter });
      }
    }
  }, [formFilter, selectedFilter, getFilterValueById]);

  useEffect(() => {
    if (formScope && !selectedScope) {
      // value.id is optional on SelectedScope; original JS passed it through
      // as-is (possibly undefined) to getScopeValueById.
      const scope = getScopeValueById(formScope?.value?.id as string);

      if (scope) {
        updateScopeState({ selectedScope: scope });
      }
    }
  }, [formScope, selectedScope, getScopeValueById]);

  return (
    <>
      <div className={"scope-input automations-scope-wrap"}>
        <Controller
          name={"scope"}
          control={control}
          render={({ fieldState: { error } }) => (
            <>
              {!isSummary && <label className={`input-label`}>Scope</label>}
              <ScopeManagement
                showAsLabel
                displayAsInput
                setDefaultsForScopeAndFilter={false}
                useSameWidth
                viewMode={isSummary}
                allowSaveWithPendingChanges
              />
              {!isSummary && !!requiredMessages && !!error?.message && (
                <span className={"input-error-message"}>{error.message}</span>
              )}
            </>
          )}
          rules={{
            required: requiredMessages?.scope ?? REQUIRED_ERROR_MESSAGE,
          }}
        />
      </div>
      <div className={"filter-input"}>
        <Controller
          name={"filter"}
          control={control}
          render={({ fieldState: { error } }) => (
            <>
              {!isSummary && <label className={`input-label`}>Filter</label>}
              <FilterManagement
                hideSaveAs
                isVertical
                isInline
                useSameWidth
                showFilterActions
                displayAsInput
                setDefaultsForScopeAndFilter={false}
                hasEditMode={true}
                viewMode={isSummary}
                filtersToPin={[STATUS_FILTER_TO_PIN]}
                allowSaveWithPendingChanges
                entity={filtersEntity}
              />
              {!isSummary && !!requiredMessages && !!error?.message && (
                <span className={"input-error-message"}>{error.message}</span>
              )}
            </>
          )}
          rules={{
            required: requiredMessages?.filter ?? REQUIRED_ERROR_MESSAGE,
          }}
        />
      </div>
      {!isSummary && !!formFilter?.value?.query && !!formScope && (
        <FindingsBySeverityBar
          // FilterObject/SelectedScope have optional value.id|query; the bar
          // requires them. Runtime guard above ensures query/scope presence;
          // casts bridge the optional→required gap without changing behavior.
          selectedFilter={
            formFilter as FindingsBySeverityProps["selectedFilter"]
          }
          selectedScope={formScope as FindingsBySeverityProps["selectedScope"]}
          viewMode={findingsCountViewMode}
        />
      )}
    </>
  );
};

export default FilterAndScopeInputs;
